import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import type { Announcement } from '@/mocks/announcements';
import { teachers } from '@/mocks/teachers';
import { mockParents } from '@/mocks/announcements';

type TabKey = 'today' | 'upcoming' | 'previous';

interface AnnouncementsSectionProps {
  announcements: Announcement[];
  currentUser: string;
  onCreateClick: () => void;
  onDelete: (id: string) => void;
}

export default function AnnouncementsSection({ announcements, currentUser, onCreateClick, onDelete }: AnnouncementsSectionProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('today');
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Read-status state — seeded from mock data, updated locally on "Mark as Read"
  const [readIds, setReadIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    announcements.forEach((ann) => {
      if (ann.readBy && ann.readBy.includes(currentUser)) {
        initial.add(ann.id);
      }
    });
    return initial;
  });

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);

  const isToday = (iso: string): boolean => {
    const d = new Date(iso);
    return d >= todayStart && d < tomorrowStart;
  };

  const isFuture = (iso: string): boolean => {
    const d = new Date(iso);
    return d >= tomorrowStart;
  };

  const tabs: { key: TabKey; label: string; emptyTitle: string; emptyDesc: string }[] = [
    { key: 'today', label: t('dashboard.todayAnnouncements'), emptyTitle: t('dashboard.noTodayAnnouncements'), emptyDesc: t('dashboard.noTodayAnnouncementsDesc') },
    { key: 'upcoming', label: t('dashboard.upcomingAnnouncements'), emptyTitle: t('dashboard.noUpcomingAnnouncements'), emptyDesc: t('dashboard.noUpcomingAnnouncementsDesc') },
    { key: 'previous', label: t('dashboard.previousAnnouncements'), emptyTitle: t('dashboard.noPreviousAnnouncements'), emptyDesc: t('dashboard.noPreviousAnnouncementsDesc') },
  ];

  // Filter announcements
  const filteredAll = useMemo(() => {
    let result = [...announcements];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.message.toLowerCase().includes(q) ||
          a.sentBy.name.toLowerCase().includes(q)
      );
    }
    if (dateFilter) {
      result = result.filter((a) => {
        const pubDate = a.publishedAt.split('T')[0];
        return pubDate === dateFilter;
      });
    }
    result.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
    return result;
  }, [announcements, search, dateFilter]);

  // Categorise
  const categorised = useMemo(() => {
    const today: Announcement[] = [];
    const upcoming: Announcement[] = [];
    const previous: Announcement[] = [];
    filteredAll.forEach((a) => {
      if (isToday(a.publishedAt)) today.push(a);
      else if (isFuture(a.publishedAt)) upcoming.push(a);
      else previous.push(a);
    });
    return { today, upcoming, previous };
  }, [filteredAll]);

  const totalAcrossTabs = categorised.today.length + categorised.upcoming.length + categorised.previous.length;

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMarkRead = (id: string) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleDelete = (id: string) => {
    onDelete(id);
    setDeleteConfirmId(null);
  };

  const getRecipientsLabel = (ann: Announcement): string => {
    const parts: string[] = [];
    if (ann.sentToFaculty === 'all' || (Array.isArray(ann.sentToFaculty) && ann.sentToFaculty.length > 0)) {
      parts.push(ann.sentToFaculty === 'all' ? t('dashboard.allFaculty') : t('dashboard.selectedFaculty', { count: ann.sentToFaculty.length }));
    }
    if (ann.sentToParents === 'all' || (Array.isArray(ann.sentToParents) && ann.sentToParents.length > 0)) {
      parts.push(ann.sentToParents === 'all' ? t('dashboard.allParents') : t('dashboard.selectedParents', { count: ann.sentToParents.length }));
    }
    if (parts.length === 0) return t('dashboard.noRecipients');
    return parts.join(' & ');
  };

  const getRecipientsDetail = (ann: Announcement): string => {
    const names: string[] = [];
    if (ann.sentToFaculty === 'all') {
      names.push('All Faculty Members');
    } else if (Array.isArray(ann.sentToFaculty)) {
      ann.sentToFaculty.forEach((id) => {
        const tch = teachers.find((t) => t.id === id);
        if (tch) names.push(tch.name);
      });
    }
    if (ann.sentToParents === 'all') {
      names.push('All Parents');
    } else if (Array.isArray(ann.sentToParents)) {
      ann.sentToParents.forEach((id) => {
        const par = mockParents.find((p) => p.id === id);
        if (par) names.push(`${par.name} (${par.studentName})`);
      });
    }
    return names.join(', ');
  };

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  const currentList = categorised[activeTab];
  const currentTab = tabs.find((tab) => tab.key === activeTab)!;

  return (
    <div>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-heading font-700 text-foreground-900 text-lg">
            {t('dashboard.announcements')}
          </h3>
          <p className="text-sm text-foreground-500 font-label mt-0.5">
            {t('dashboard.announcementsSubtitle')}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          iconLeft="ri-add-line"
          onClick={onCreateClick}
        >
          {t('dashboard.createAnnouncement')}
        </Button>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
        <div className="relative flex-1 w-full">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            placeholder={t('dashboard.searchAnnouncements')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setActiveTab('today'); }}
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-700 placeholder:text-foreground-400 font-label outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full sm:w-auto pl-3 pr-3 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-700 font-label outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
              title={t('dashboard.filterByDate')}
            />
          </div>
          {dateFilter && (
            <button
              onClick={() => setDateFilter('')}
              className="px-3 py-2.5 text-xs font-label font-medium text-foreground-500 hover:text-foreground-700 bg-background-50 border border-background-300 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line mr-1" />
              {t('dashboard.clearDateFilter')}
            </button>
          )}
          <span className="text-xs text-foreground-400 font-label whitespace-nowrap">
            {totalAcrossTabs} {totalAcrossTabs === 1 ? t('common.item') : t('common.items')}
          </span>
        </div>
      </div>

      {/* Tab Switcher */}
      {!search && !dateFilter && totalAcrossTabs > 0 && (
        <div className="mb-5 inline-flex items-center gap-1 bg-background-50 border border-background-300 rounded-full px-1 py-1">
          {tabs.map((tab) => {
            const count = categorised[tab.key].length;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`relative flex items-center gap-1.5 px-4 py-2 text-sm font-label font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab.label}
                {count > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-heading font-700 rounded-full ${
                    activeTab === tab.key
                      ? 'bg-background-50/25 text-background-50'
                      : 'bg-background-100 text-foreground-500'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Announcements List */}
      {totalAcrossTabs === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 bg-background-50 border border-background-200 rounded-2xl">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-megaphone-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm font-heading font-700 text-foreground-600">
            {t('dashboard.noAnnouncements')}
          </p>
          <p className="text-xs text-foreground-400 font-label mt-1">
            {t('dashboard.noAnnouncementsDesc')}
          </p>
        </div>
      ) : currentList.length === 0 ? (
        /* Per-tab empty state */
        <div className="flex flex-col items-center justify-center py-14 px-4 bg-background-50 border border-background-200 rounded-2xl">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 mb-3">
            <i className={`text-xl text-foreground-400 ${
              activeTab === 'today' ? 'ri-calendar-check-line' : activeTab === 'upcoming' ? 'ri-calendar-event-line' : 'ri-history-line'
            }`} />
          </div>
          <p className="text-sm font-heading font-700 text-foreground-600">
            {currentTab.emptyTitle}
          </p>
          <p className="text-xs text-foreground-400 font-label mt-1">
            {currentTab.emptyDesc}
          </p>
        </div>
      ) : (
        /* Cards scroll container — 3 visible, scroll for rest */
        <div
          className={`flex flex-col gap-3 ${
            currentList.length > 3 ? 'max-h-[460px] overflow-y-auto pr-1' : ''
          }`}
          style={currentList.length > 3 ? { scrollbarWidth: 'thin', scrollbarColor: 'var(--background-300) transparent' } : undefined}
        >
          {currentList.map((ann) => renderCard(ann))}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative z-10 w-full max-w-sm bg-background-50 rounded-2xl p-6 shadow-floating">
            <h3 className="font-heading font-700 text-foreground-900 text-base">{t('dashboard.deleteAnnouncement')}</h3>
            <p className="text-sm text-foreground-600 font-label mt-2">{t('dashboard.deleteAnnouncementConfirm')}</p>
            <div className="flex items-center justify-end gap-3 mt-5">
              <Button variant="outline" size="sm" onClick={() => setDeleteConfirmId(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" size="sm" onClick={() => handleDelete(deleteConfirmId)}>
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderCard(ann: Announcement) {
    const isExpanded = expandedIds.has(ann.id);
    const isRead = readIds.has(ann.id);
    const shouldTruncate = ann.message.length > 280;
    const displayMessage = shouldTruncate && !isExpanded
      ? ann.message.slice(0, 280) + '...'
      : ann.message;

    return (
      <div
        key={ann.id}
        className={`bg-background-50 border rounded-xl p-4 sm:p-5 hover:border-background-300 transition-colors group flex-shrink-0 ${
          isRead ? 'border-background-200' : 'border-background-200 border-l-4 border-l-amber-500'
        }`}
      >
        {/* Top row: sender + time + actions */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
              <span className="text-xs font-heading font-700 text-accent-700">{ann.sentBy.avatar}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-label font-medium text-foreground-800 truncate">
                {ann.sentBy.name}
                <span className="text-foreground-400 font-normal"> · {ann.sentBy.role}</span>
              </p>
              <p className="text-xs text-foreground-500 font-label">
                {formatDate(ann.publishedAt)} {t('common.at') || 'at'} {formatTime(ann.publishedAt)}
              </p>
            </div>
            {ann.status === 'scheduled' && (
              <Badge variant="warning" size="sm">
                {t('dashboard.scheduled')}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Read-status indicator */}
            {isRead ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-label font-medium whitespace-nowrap">
                <i className="ri-check-double-line text-xs" />
                {t('dashboard.read')}
              </span>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkRead(ann.id);
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 text-[11px] font-label font-medium transition-colors cursor-pointer whitespace-nowrap opacity-0 group-hover:opacity-100"
                title={t('dashboard.markAsRead')}
              >
                <i className="ri-check-line text-xs" />
                {t('dashboard.markAsRead')}
              </button>
            )}

            <button
              onClick={() => setDeleteConfirmId(ann.id)}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-semantic-error hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0 opacity-0 group-hover:opacity-100"
              title={t('common.delete')}
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </div>

        {/* Message */}
        <div className="mb-3">
          <p className="text-sm text-foreground-700 font-label leading-relaxed whitespace-pre-wrap">
            {displayMessage}
          </p>
          {shouldTruncate && (
            <button
              onClick={() => toggleExpand(ann.id)}
              className="mt-1 text-xs font-label font-medium text-primary-600 hover:text-primary-700 cursor-pointer transition-colors"
            >
              {isExpanded ? t('common.showLess') : t('common.showMore')}
            </button>
          )}
        </div>

        {/* Audio attachment indicator */}
        {ann.audioUrl && (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-secondary-50 border border-secondary-200 rounded-lg">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
              <i className="ri-mic-fill text-secondary-600 text-sm" />
            </div>
            <span className="text-xs font-label font-medium text-secondary-700">{t('dashboard.audioAnnouncement')}</span>
            <button
              className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg text-secondary-600 hover:bg-secondary-100 transition-colors cursor-pointer flex-shrink-0"
              title={t('dashboard.playAudio')}
            >
              <i className="ri-play-fill text-sm" />
            </button>
          </div>
        )}

        {/* Footer: recipients + expand recipients */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-3 border-t border-background-100">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span className="text-xs text-foreground-400 font-label flex-shrink-0">{t('dashboard.sentTo')}:</span>
            <span className="text-xs font-label font-medium text-foreground-600 truncate">
              {getRecipientsLabel(ann)}
            </span>
          </div>

          {getRecipientsDetail(ann) && (
            <button
              className="text-xs font-label font-medium text-secondary-600 hover:text-secondary-700 cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap flex-shrink-0"
              title={getRecipientsDetail(ann)}
            >
              <i className="ri-group-line" />
              <span className="hidden sm:inline">{t('common.viewDetails')}</span>
            </button>
          )}
        </div>
      </div>
    );
  }
}